import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TaleSelect } from "@/components/TaleSelect";
import { giftsService, giftRequestsService } from "@/api/awards";
import {
  FiSearch,
  FiGift,
  FiSmartphone,
  FiCreditCard,
  FiShoppingBag,
  FiList,
  FiChevronRight,
  FiZap,
  FiCheckCircle,
  FiClock,
  FiImage
} from "react-icons/fi";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import {useSelector} from "react-redux";
import TaleButton from "@components/TaleButton";

// Default images for different gift types
const DEFAULT_IMAGES = {
  SHOPPING_VOUCHER: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=400&h=300&fit=crop&crop=center",
  PHONE_RECHARGE_CARD: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&h=300&fit=crop&crop=center",
  COUPON: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop&crop=center",
  DEFAULT: "https://images.unsplash.com/photo-1534423861386-85a16f5d13fd?w=400&h=300&fit=crop&crop=center"
};

const Awards = () => {
  const navigate = useNavigate();
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
  });
  const [requestingGiftId, setRequestingGiftId] = useState(null);
  const [userPoints, setUserPoints] = useState(0); // Mock user points
  const { user } = useSelector((state) => state.auth);
  useEffect(() => {
    if (user?.totalPoints !== undefined) {
      setUserPoints(user.totalPoints);
    }
  }, [user?.totalPoints]);  const giftTypes = [
    { value: "ALL", label: "All Gifts" },
    { value: "SHOPPING_VOUCHER", label: "Shopping Vouchers" },
    { value: "PHONE_RECHARGE_CARD", label: "Phone Recharge Cards" },
    { value: "COUPON", label: "Coupons" },
  ];

  const loadGifts = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const params = { page, size: 12 };
      if (typeFilter !== "ALL") params.type = typeFilter;

      const response = await giftsService.getDisplayedGifts(
          params.page,
          params.size,
          params.type
      );

      setGifts(response.result || []);
      setPagination({
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalItems,
      });
    } catch (error) {
      console.error("Failed to load gifts:", error);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    loadGifts();
  }, [loadGifts]);

  const filteredGifts = gifts.filter(gift =>
      gift.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gift.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRequestGift = async (giftId, giftCost) => {
    if (userPoints < giftCost) {
      alert(`You need ${giftCost - userPoints} more points to request this gift!`);
      return;
    }

    try {
      const confirmed = window.confirm(`Are you sure you want to request this gift for ${giftCost} points?`);
      if (!confirmed) return;

      setRequestingGiftId(giftId);
      await giftRequestsService.createGiftRequest(giftId, "User requested this gift");

      // Update user points (in real app, this would come from API)
      setUserPoints(prev => prev - giftCost);

      alert("Gift request submitted successfully!");
      loadGifts();
    } catch (error) {
      alert(`Failed to request gift: ${error.message}`);
    } finally {
      setRequestingGiftId(null);
    }
  };

  const handleViewMyRequests = () => {
    navigate("/awards/my-requests");
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "SHOPPING_VOUCHER": return <FiShoppingBag className="w-5 h-5 text-purple-600" />;
      case "PHONE_RECHARGE_CARD": return <FiSmartphone className="w-5 h-5 text-green-600" />;
      case "COUPON": return <FiCreditCard className="w-5 h-5 text-blue-600" />;
      default: return <FiGift className="w-5 h-5 text-amber-600" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "SHOPPING_VOUCHER": return "Shopping Voucher";
      case "PHONE_RECHARGE_CARD": return "Phone Recharge";
      case "COUPON": return "Coupon";
      default: return "Gift";
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "SHOPPING_VOUCHER": return "bg-purple-100 text-purple-800";
      case "PHONE_RECHARGE_CARD": return "bg-green-100 text-green-800";
      case "COUPON": return "bg-blue-100 text-blue-800";
      default: return "bg-amber-100 text-amber-800";
    }
  };

  const getGiftImage = (gift) => {
    if (gift.fileUrl) return gift.fileUrl;
    return DEFAULT_IMAGES[gift.type] || DEFAULT_IMAGES.DEFAULT;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateArray) => {
    if (!dateArray || dateArray.length < 6) return "N/A";
    const [year, month, day, hour, minute, second] = dateArray;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
      <TaleDashbaordMainCard className="space-y-6">
        {/* Header with points and requests button */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Rewards & Gifts</h1>
            <p className="text-gray-600 mt-1">Exchange your points for exciting rewards</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            {/* User Points Card
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 flex-1">
              <div className="bg-amber-100 p-2 rounded-lg">
                <FiZap className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">Available Points</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-900">{userPoints.toLocaleString()}</p>
              </div>
            </div>*/}

            {/* My Requests Button */}
            <TaleButton
                onClick={handleViewMyRequests}
                variant="filled"
                role="normal"
                className="!px-2"
            >
              <span className="flex items-center w-max justify-between gap-2">
                <FiShoppingBag className="w-4 h-4"/>
                My Requests
              </span>
            </TaleButton>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 lg:p-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-96">
              <div className="relative">
                <input
                    type="text"
                    placeholder="Search gifts by name or description..."
                    className="px-4 py-3 border border-gray-300 rounded-xl w-full pl-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="flex items-center gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2 text-gray-600">
                <FiList className="w-5 h-5" />
                <span className="font-medium hidden sm:inline">Filter by:</span>
              </div>
              <TaleSelect
                  options={giftTypes}
                  value={typeFilter}
                  onChange={setTypeFilter}
                  className="!rounded-xl min-w-[180px]"
              />
            </div>
          </div>
        </div>

        {loading ? (
            <div className="flex flex-col justify-center items-center py-20 space-y-4">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
              <p className="text-gray-600">Loading amazing gifts for you...</p>
            </div>
        ) : (
            <>
              {/* Results Count */}
              <div className="flex items-center justify-between px-2">
                <p className="text-gray-600">
                  Showing <span className="font-bold text-gray-900">{filteredGifts.length}</span> of{" "}
                  <span className="font-bold text-gray-900">{pagination.totalItems}</span> gifts
                </p>
                {typeFilter !== "ALL" && (
                    <button
                        onClick={() => setTypeFilter("ALL")}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear filter
                    </button>
                )}
              </div>

              {/* Gifts Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                {filteredGifts.map((gift) => {
                  const canAfford = userPoints >= gift.costPoints;
                  const giftImage = getGiftImage(gift);
                  const isDefaultImage = !gift.fileUrl;

                  return (
                      <div
                          key={gift.id}
                          className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-200 h-full flex flex-col bg-white rounded-xl"
                      >
                        {/* Gift Image */}
                        <div className="h-48 relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                          <img
                              src={giftImage}
                              alt={gift.name}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DEFAULT_IMAGES.DEFAULT;
                              }}
                          />
                          {isDefaultImage && (
                              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent flex items-center justify-center">
                                <FiImage className="w-12 h-12 text-white/50" />
                              </div>
                          )}
                          {/* Gift Type Badge */}
                          <div className="absolute top-3 left-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeColor(gift.type)}`}>
                        {getTypeLabel(gift.type)}
                      </span>
                          </div>
                        </div>

                        <div className="p-4 lg:p-5 flex-1 flex flex-col">
                          {/* Gift Name and Points */}
                          <div className="mb-3">
                            <h3 className="text-lg lg:text-xl font-bold text-gray-900 line-clamp-1">
                              {gift.name}
                            </h3>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-2">
                                {getTypeIcon(gift.type)}
                                <span className="text-sm text-gray-500">
                            Added {formatDate(gift.createdAt)}
                          </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <FiZap className="w-4 h-4 text-amber-500" />
                                <span className={`font-bold ${canAfford ? 'text-gray-900' : 'text-red-600'}`}>
                            {gift.costPoints.toLocaleString()} pts
                          </span>
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          <p className="text-gray-600 mb-4 line-clamp-2 text-sm flex-1">
                            {gift.description || "No description available"}
                          </p>

                          {/* Card Amount (if applicable) */}
                          {(gift.type === "SHOPPING_VOUCHER" || gift.type === "PHONE_RECHARGE_CARD") && gift.cardAMount && (
                              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-blue-800">Card Value:</span>
                                  <span className="text-lg font-bold text-blue-900">
                            {formatCurrency(gift.cardAMount)} TND
                          </span>
                                </div>
                              </div>
                          )}

                          {/* Points Comparison */}
                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600">Your points:</span>
                              <span className={`font-semibold ${canAfford ? 'text-green-600' : 'text-red-600'}`}>
                          {userPoints.toLocaleString()}
                        </span>
                            </div>
                            {!canAfford && (
                                <p className="text-xs text-red-500">
                                  Need {(gift.costPoints - userPoints).toLocaleString()} more points
                                </p>
                            )}
                          </div>

                          {/* Request Button */}
                          <button
                              onClick={() => handleRequestGift(gift.id, gift.costPoints)}
                              disabled={!canAfford || requestingGiftId === gift.id}
                              className={`w-full py-3 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 mt-auto
                        ${canAfford
                                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90 hover:shadow-lg'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              }
                      `}
                          >
                            {requestingGiftId === gift.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Processing...
                                </>
                            ) : canAfford ? (
                                <>
                                  <FiCheckCircle className="w-5 h-5" />
                                  Request Gift
                                </>
                            ) : (
                                <>
                                  <FiClock className="w-5 h-5" />
                                  Need More Points
                                </>
                            )}
                          </button>
                        </div>
                      </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                    <button
                        onClick={() => loadGifts(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 0}
                        className="px-4 py-2.5 rounded-xl border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                    >
                      <FiChevronRight className="w-4 h-4 rotate-180" />
                      Previous
                    </button>

                    <div className="flex items-center gap-1 mx-2">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i;
                        } else if (pagination.currentPage <= 2) {
                          pageNum = i;
                        } else if (pagination.currentPage >= pagination.totalPages - 3) {
                          pageNum = pagination.totalPages - 5 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }

                        return (
                            <button
                                key={pageNum}
                                onClick={() => loadGifts(pageNum)}
                                className={`w-9 h-9 rounded-lg flex items-center justify-center font-medium transition-colors text-sm
                        ${pagination.currentPage === pageNum
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                              {pageNum + 1}
                            </button>
                        );
                      })}
                    </div>

                    <button
                        onClick={() => loadGifts(pagination.currentPage + 1)}
                        disabled={pagination.currentPage >= pagination.totalPages - 1}
                        className="px-4 py-2.5 rounded-xl border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                    >
                      Next
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
              )}

              {/* Empty State */}
              {filteredGifts.length === 0 && !loading && (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                    <div className="inline-flex p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl mb-6">
                      <FiGift className="w-20 h-20 text-blue-400" />
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">No gifts found</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto px-4">
                      {searchTerm
                          ? `No gifts match "${searchTerm}". Try different keywords or browse all gifts.`
                          : typeFilter !== "ALL"
                              ? `No ${getTypeLabel(typeFilter).toLowerCase()}s available. Try another category.`
                              : "No gifts available at the moment. Check back soon for new rewards!"}
                    </p>
                    {(searchTerm || typeFilter !== "ALL") && (
                        <button
                            onClick={() => {
                              setSearchTerm("");
                              setTypeFilter("ALL");
                            }}
                            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
                        >
                          View All Gifts
                        </button>
                    )}
                  </div>
              )}
            </>
        )}
      </TaleDashbaordMainCard>
  );
};

export default Awards;
